import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Link2, ExternalLink, Edit } from 'lucide-react'
import { getDomain } from '@super/shared-types'
import type { KnowledgeBaseWithLinks, Pronunciation } from '@super/shared-types'
import { isMobileBreakpoint } from '@/lib/utils/window-utils'

interface LinkData {
  url: string
  pronunciation?: Pronunciation | null
  domain: string
}

interface LinksTabProps {
  knowledgeBases: KnowledgeBaseWithLinks[]
  loading: boolean
  onLinkEdit: (url: string) => void
}

export function LinksTab({
  knowledgeBases,
  loading,
  onLinkEdit,
}: LinksTabProps) {
  const getAllLinksWithData = (): LinkData[] => {
    const links: LinkData[] = []

    knowledgeBases.forEach(kb => {
      if (kb.links) {
        kb.links.forEach(kbl => {
          const existingLink = links.find(l => l.url === kbl.link.url)
          if (!existingLink) {
            links.push({
              url: kbl.link.url,
              pronunciation: kbl.link.pronunciation,
              domain: getDomain(kbl.link.url),
            })
          }
        })
      }
    })

    return links.sort((a, b) => a.domain.localeCompare(b.domain))
  }

  const linkData = getAllLinksWithData()

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Domain</TableHead>
                <TableHead>URL</TableHead>
                <TableHead>Pronunciation</TableHead>
                <TableHead className="w-[120px]" align="center">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell className="hidden lg:table-cell">
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-48" />
                      <div className="lg:hidden">
                        <Skeleton className="h-4 w-24" />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    )
  }

  if (linkData.length === 0) {
    return (
      <div className="space-y-4">
        <div className="border rounded-lg p-8">
          <p className="text-sm text-muted-foreground text-center">
            No links found. Links will appear here when you create knowledge
            bases with URLs.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[160px] hidden lg:table-cell">
                Domain
              </TableHead>
              <TableHead>URL</TableHead>
              <TableHead className="hidden lg:table-cell">
                Pronunciation
              </TableHead>
              <TableHead
                className="w-[120px] hidden lg:table-cell"
                align="center"
              >
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {linkData.map((link, index) => (
              <TableRow
                key={index}
                className="lg:cursor-default cursor-pointer lg:hover:bg-transparent hover:bg-muted/50"
                onClick={() => {
                  // Only handle click on mobile/tablet (when actions column is hidden)
                  if (isMobileBreakpoint()) {
                    onLinkEdit(link.url)
                  }
                }}
              >
                <TableCell className="hidden lg:table-cell">
                  <div className="flex items-center gap-2">
                    <Link2 className="h-4 w-4 text-muted-foreground" />
                    <Badge variant="outline" className="text-xs">
                      {link.domain}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell className="max-w-xs">
                  <div className="space-y-1">
                    <div className="truncate" title={link.url}>
                      <span className="text-xs hover:text-blue-800">
                        {link.url}
                      </span>
                    </div>
                    {/* Show domain and pronunciation on mobile/tablet */}
                    <div className="lg:hidden flex flex-col space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          <Link2 className="h-3 w-3 mr-1" />
                          {link.domain}
                        </Badge>
                      </div>
                      {link.pronunciation && (
                        <span className="text-xs font-mono bg-muted px-2 py-1 rounded inline-block">
                          {link.pronunciation.phoneme}
                        </span>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden lg:table-cell">
                  {link.pronunciation ? (
                    <span className="text-sm font-mono bg-muted px-2 py-1 rounded">
                      {link.pronunciation.phoneme}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground italic">
                      No pronunciation set
                    </span>
                  )}
                </TableCell>
                <TableCell className="hidden lg:table-cell">
                  <div className="flex gap-1 justify-center">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onLinkEdit(link.url)}
                      title="Edit pronunciation"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      asChild
                      title="Open in new tab"
                    >
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
